import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["amount", "unit", "previous", "choice"];
  static values = { precision: { type: Number, default: 4 } };

  connect() {
    this.reset();
  }

  // A field rewritten from outside, such as the protocol dose put back on Log
  // dose, starts a fresh conversion from what it now holds.
  reset() {
    this.conversions = new Map(
      this.amountTargets.map((input) => [
        input,
        {
          origin: {
            amount: input.value,
            unit: this.hasPreviousTarget ? this.previousTarget.value : this.unitTarget.value,
          },
          lastConverted: null,
        },
      ])
    );
  }

  change() {
    const from = this.previousTarget.value;
    const to = this.unitTargets.find((input) => input.checked)?.value ?? this.unitTarget.value;
    this.convertAmounts(from, to);
    this.previousTarget.value = to;
  }

  switch(event) {
    event.preventDefault();
    const from = this.unitTarget.value;
    const to = event.currentTarget.value.replace(/^unit:/, "");
    this.convertAmounts(from, to);
    this.unitTarget.value = to;
    this.choiceTargets.forEach((choice) =>
      choice.setAttribute("aria-pressed", String(choice.value === event.currentTarget.value))
    );
    this.unitTarget.dispatchEvent(new Event("change", { bubbles: true }));
  }

  convertAmounts(from, to) {
    if (from === to) return;

    this.amountTargets.forEach((input) => {
      const conversion = this.conversions.get(input);
      if (conversion.lastConverted === null || input.value !== conversion.lastConverted) {
        conversion.origin = { amount: input.value, unit: from };
      }
      const { origin } = conversion;
      const converted = to === origin.unit ? origin.amount : this.convert(origin.amount, origin.unit, to);
      input.value = converted;
      conversion.lastConverted = converted;
    });
  }

  convert(amount, from, to) {
    if (!amount.trim() || ![from, to].every((unit) => ["mg", "mcg"].includes(unit))) return "";
    const number = Number(amount);
    if (!Number.isFinite(number)) return "";
    const converted = Number((number * (from === "mg" ? 1000 : 0.001)).toFixed(this.precisionValue));
    return number !== 0 && converted === 0 ? "" : String(converted);
  }
}
